// PollingPusherChannelData.jsx
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { LRUCache } from 'lru-cache';

import { pusher } from '../resource/debug.js';
import { NULL_FUNCTION, EMPTY_ARRAY } from '../resource/defaults.js';
import { getPrivateUserChannel } from '../resource/pusherUtils.js';

const pusherLog = pusher.extend('log:PollingPusherChannelData');

const channelIntervalMap = new Map();
const channelFetchTimestamp = new LRUCache({ max: 256 });

export const PollingPusherChannelData = ({
  inViewportUserIds = EMPTY_ARRAY,
  pollingMsec,
  fetchRetainedEvents = NULL_FUNCTION,
}) => {
  useEffect(() => {
    return () => {
      pusherLog('clear all intervals', { pollingMsec });
      channelIntervalMap.forEach((interval, userId) => {
        clearInterval(interval);
        channelIntervalMap.delete(userId);
        pusherLog('user id removed', { userId });
      });
    };
  }, [pollingMsec]);

  useEffect(() => {
    pusherLog('in-viewport user ids or polling msec change', {
      userIds: [...inViewportUserIds],
      pollingMsec,
    });

    if (!pollingMsec) {
      return;
    }
    pusherLog('start to polling', { pollingMsec });

    // removed user ids out of viewport
    // removed first to avoid extra compare
    channelIntervalMap.forEach((interval, userId) => {
      if (!inViewportUserIds.includes(userId)) {
        clearInterval(interval);
        channelIntervalMap.delete(userId);
        pusherLog('user id removed', { userId });
      }
    });

    // added missing user ids inside viewport
    const now = Date.now();
    inViewportUserIds
      .filter(userId => {
        const shouldAdd = !channelIntervalMap.get(userId);
        return userId.length === 24 && shouldAdd;
      })
      .forEach(userId => {
        const channel = getPrivateUserChannel({ userId });

        // fetch outdated or missing events
        const previousFetchTime = channelFetchTimestamp.get(userId);
        if (!previousFetchTime || now - previousFetchTime > pollingMsec) {
          pusherLog('direct fetch', {
            userId,
            previousFetchTime,
            now,
            pollingMsec,
          });
          channelFetchTimestamp.set(userId, now);
          fetchRetainedEvents({ channels: [channel] });
        }

        pusherLog('add interval', { userId, pollingMsec });
        const interval = setInterval(() => {
          channelFetchTimestamp.set(userId, now);
          fetchRetainedEvents({ channels: [channel] });
        }, pollingMsec);
        channelIntervalMap.set(userId, interval);
      });
  }, [fetchRetainedEvents, inViewportUserIds, pollingMsec]);

  return null;
};

PollingPusherChannelData.propTypes = {
  inViewportUserIds: PropTypes.array,
  pollingMsec: PropTypes.number,
  fetchRetainedEvents: PropTypes.func,
};

export default PollingPusherChannelData;
